import { Counter } from '@/components/form/fields/number/Counter';
import { CategoryIcons } from '@/components/icons/CategoryIcons';
import { Picture } from '@/components/image/Image';
import DaasTableRowCell from '@/components/table/DaasTableRowCell';
import EllipsizedWithTooltip from '@/components/tooltip/EllipsizedWithTooltip';
import SiTrash2 from '@/icons/SiTrash2';
import { Product } from '@/services/api/contracts/catalog/types';
import {
  usePoSelectedProductQuantity,
  usePoSelectProduct,
  usePoSetProductQuantity
} from '@/services/purchaseOrders/usePurchaseOrderService';
import { usePrintCurrencyStringForCurrentCustomer } from '@/utils/price';
import { FC, FunctionComponent } from 'react';
import { Tooltip } from 'react-tooltip';
import PurchaseOrderConfirmOrderDeviceTablePCMField from './pcm/PurchaseOrderConfirmOrderDeviceTablePCMField';
import PurchaseOrderConfirmOrderDeviceTablePeripheralPCMField from './pcm/PurchaseOrderConfirmOrderDeviceTablePeripheralPCMField';
import { PurchaseOrderConfirmOrderDeviceTableGroupedData } from './PurchaseOrderConfirmOrderDeviceTableGrouped';

interface Props {
  data: PurchaseOrderConfirmOrderDeviceTableGroupedData;
}

interface ProductImageProps {
  product: Product;
  height: number;
  width: number;
}

const ProductImage: FC<ProductImageProps> = ({ product, height, width }) => {
  return product.imageUri?.id ? (
    <Picture image={product.imageUri} containerHeight={height} containerWidth={width}></Picture>
  ) : (
    <div className="w-full [&_svg]:w-[100%] [&_svg]:h-[100%]" style={{ height }}>
      {CategoryIcons[product.category.description]}
    </div>
  );
};

export const PurchaseOrderConfirmOrderDeviceTableRow: FunctionComponent<Props> = ({ data }) => {
  const price = data.productRef.price?.price ?? 0;
  const quantity = usePoSelectedProductQuantity(data.productRef);

  const printCurrencyString = usePrintCurrencyStringForCurrentCustomer();
  const formattedDevicePrice = printCurrencyString(price);

  const totalPrice = price * (quantity ?? 1);

  const totalFormattedCost = printCurrencyString(totalPrice);

  const setQuantity = usePoSetProductQuantity(data.productRef);

  const onQuantityChange = (value: number) => {
    setQuantity(value);
  };

  const specs = data.productRef.propertyBag.properties?.map(spec => spec.text).join(' / ');

  const removeProduct = usePoSelectProduct(data.productRef);

  return (
    <>
      <DaasTableRowCell>
        <div className="flex flex-row space-x-5 w-full h-full items-center">
          <div className="w-[125px] h-full flex items-center justify-center">
            <ProductImage product={data.productRef} height={70} width={125} />
          </div>
          <div className="flex-1">
            <p className="text-lg">{data.productRef.manufacturer.name}</p>
            <p className="text-2xl font-semibold">{data.productRef.name}</p>
            <EllipsizedWithTooltip
              // Calc is full viewport - LHP / total table fraction to get value of 1fr
              // Since device table row is 1fr
              className="max-w-[calc((100vw-25rem)/3)]"
              id={`${data.productRef.id}-specs`}
              elementText={specs}
              tooltipText={specs}
            />
          </div>
        </div>
      </DaasTableRowCell>
      <DaasTableRowCell>{data.productRef.productId}</DaasTableRowCell>
      <DaasTableRowCell>
        <Counter
          className="flex justify-center py-2"
          id={data.id}
          value={quantity!}
          allowManualChange={true}
          min={1}
          onChange={onQuantityChange}
        />
      </DaasTableRowCell>
      <DaasTableRowCell>{formattedDevicePrice}</DaasTableRowCell>
      <DaasTableRowCell>{totalFormattedCost}</DaasTableRowCell>
      <DaasTableRowCell>
        {data.productRef.category.isMainDevice ? (
          <PurchaseOrderConfirmOrderDeviceTablePCMField
            categoryId={data.productRef.category.id}
            manufacturerId={data.productRef.manufacturer.id}
            totalPrice={totalPrice}
          />
        ) : (
          <PurchaseOrderConfirmOrderDeviceTablePeripheralPCMField totalPrice={totalPrice} />
        )}
      </DaasTableRowCell>
      <DaasTableRowCell>
        <div>
          <a
            onClick={() => {
              removeProduct();
            }}
            className="hover:text-red-500"
            data-tooltip-id={`delete-product-tooltip-${data.id}`}
          >
            <SiTrash2 className="text-xl" />
          </a>

          <Tooltip id={`delete-product-tooltip-${data.id}`}>
            <p>Remove from PO</p>
          </Tooltip>
        </div>
      </DaasTableRowCell>
    </>
  );
};
